import { identifiers } from 'utils/constants/identifiers';
import { fetchFromStorage } from 'utils/storage';

type CurrencyContext = {
  currencyLocale?: string;
  currency?: string;
  currencySymbol?: string;
};

export const getCurrencyContext = (): CurrencyContext => {
  try {
    return JSON.parse(fetchFromStorage(identifiers.currency) as any) || {};
  } catch {
    return {};
  }
};

const getLocale = (cur: CurrencyContext) => cur?.currencyLocale?.replace(/_/g, '-') || 'en-IN';

const getCurrencyCode = (cur: CurrencyContext) => cur?.currency || 'INR';

export const getCurrencySymbol = () => {
  const cur = getCurrencyContext();
  if (cur?.currencySymbol) return cur.currencySymbol;

  try {
    const symbol = new Intl.NumberFormat(getLocale(cur), {
      style: 'currency',
      currency: getCurrencyCode(cur),
    })
      .formatToParts(0)
      .find(part => part.type === 'currency')?.value;
    return symbol || getCurrencyCode(cur);
  } catch {
    return getCurrencyCode(cur);
  }
};

export const formatCurrencyWithSymbol = (
  amount: number | null | undefined,
  options: Intl.NumberFormatOptions = {},
) => {
  const cur = getCurrencyContext();
  const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  const formattedAmount = new Intl.NumberFormat(getLocale(cur), {
    maximumFractionDigits: 0,
    ...options,
  }).format(safeAmount);
  return `${getCurrencySymbol()}${formattedAmount}`;
};

const Currency = (amount: number | null | undefined) => {
  const cur = getCurrencyContext();
  // Guard against null/undefined/NaN — Intl.NumberFormat.format(undefined|NaN) emits "NaN"
  // which then renders as "₹NaN" in booking summaries.
  const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  return new Intl.NumberFormat(getLocale(cur), {
    style: 'currency',
    currency: getCurrencyCode(cur),
  }).format(safeAmount);
};

export default Currency;
